import React, { createRef } from 'react';
import axios from 'axios';
import { Link, LinkProps } from '@mui/material';
import { getOktaAuth } from '../../providers/AuthProvider';

interface Props extends LinkProps {
  url: string;
  fileName?: string;
  defaultFileName: string;
  children: React.ReactNode;
  openInNewTab: boolean;
}

export function AuthenticatedLink({
  url,
  fileName,
  defaultFileName,
  children,
  openInNewTab,
  ...other
}: Props) {
  const link = createRef<HTMLAnchorElement>();

  const handleAction = async (event: any) => {
    if (!link?.current) {
      return;
    }

    if (link.current.href) {
      if (openInNewTab) {
        window.open(link.current.href, '_blank');
        event.preventDefault();
      }
      return;
    }

    const { accessToken } = await getOktaAuth().tokenManager.getTokens();

    const response = await axios.get(url, {
      responseType: 'arraybuffer',
      headers: {
        Authorization: 'Bearer ' + accessToken?.accessToken,
      },
    });
    const contentType = response.headers['content-type'];
    const blob = new Blob([response.data], {
      type: typeof contentType === 'string' ? contentType : undefined,
    });
    const href = window.URL.createObjectURL(blob);

    let headerFileName = null;
    if (!fileName) {
      // look through headers (case-insensitive) to find a suggested filename
      for (const [key, value] of Object.entries(response.headers)) {
        if (key?.toLowerCase() === 'content-disposition') {
          const dispositions = Array.isArray(value)
            ? value
            : value?.split('; ') || [];
          for (const disposition of dispositions) {
            if (disposition.startsWith('filename=')) {
              headerFileName = disposition.substring('filename='.length);
              break;
            }
          }
          break;
        }
      }
    }

    link.current.download = fileName ?? headerFileName ?? defaultFileName;
    link.current.href = href;

    link.current.click();
  };

  return (
    <Link {...other} ref={link} onClick={handleAction}>
      {children}
    </Link>
  );
}
